import { css } from '@emotion/react'
import { Heading, HFlow, ModalCloseButton, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { AccordionInformation } from 'components/accordion/AccordionInformation'
import { FormDebouncedValueSpy, SimNaoEnum } from 'components/form'
import { SwitchButtonField } from 'components/form/field/SwitchButtonField'
import { FormState } from 'final-form'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useMemo } from 'react'
import { metaPath } from 'util/metaPath'

import { MCHAT_PERGUNTAS, MChatAplicacaoModel } from '../model-mchat'

const meta = metaPath<MChatAplicacaoModel>()

interface MChatModalTabAdicionarProps {
  isEditing: boolean
  handleFormChange: (formState: FormState<MChatAplicacaoModel>) => void
}

export function MChatModalTabAdicionar(props: Readonly<MChatModalTabAdicionarProps>) {
  const { isEditing, handleFormChange } = props

  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme), [theme])
  const headingText = isEditing ? 'Editar avaliação M-CHAT-R™' : 'Avaliar M-CHAT-R™'

  return (
    <VFlow>
      <FormDebouncedValueSpy wait={0} onChange={handleFormChange} />
      <HFlow alignItems='flex-start' justifyContent='space-between' style={styles.headingWrapper}>
        <Heading level={1} style={styles.heading}>
          {headingText}
        </Heading>
        <ModalCloseButton />
      </HFlow>
      <VFlow>
        <AccordionInformation
          uuid='mchat-info'
          type='normal'
          header='Sobre o M-CHAT™'
          panelStyle={styles.accordionInformationPanel}
        >
          <Heading level={5}>
            O M-CHAT-R™ é um instrumento utilizado por profissionais de saúde para avaliar o risco de Transtorno do
            Espectro Autista (TEA) em crianças de 16 a 30 meses. Seu principal objetivo é auxiliar os profissionais de
            saúde da APS na identificação de sinais de risco de TEA.
          </Heading>
          <Heading level={5} fontWeight='normal'>
            Crianças classificadas como de alto risco devem ser encaminhadas para avaliação com equipe multiprofissional
            ou serviço especializado, pois podem apresentar TEA ou outros transtornos do desenvolvimento.
            <br />
            É importante destacar que o M-CHAT-R™ apresenta alta taxa de falso positivo, ou seja, nem todas as crianças
            com pontuação de risco elevado serão diagnosticadas com TEA. Para melhorar a precisão do rastreio, foi
            incluída a consulta de seguimento (M-CHAT-R/F™). Essa etapa ajuda a reduzir encaminhamentos desnecessários,
            permitindo uma reavaliação mais criteriosa dos casos com resultado positivo inicial.
            <br />
            Profissionais que aplicam o instrumento devem estar cientes de que, mesmo após a consulta de seguimento,
            algumas crianças com pontuação positiva poderão não ter TEA, mas ainda assim podem apresentar outros
            transtornos ou atrasos no desenvolvimento, exigindo acompanhamento multiprofissional e especializado.
            <br />
            Além disso, independentemente do resultado obtido no M-CHAT-R™, se houver suspeita por parte da família ou
            da equipe profissional, a criança deve ser encaminhada para avaliação multiprofissional. Isso se aplica
            inclusive aos casos classificados como de baixo risco.
          </Heading>
        </AccordionInformation>
        <VFlow vSpacing={0} style={styles.box}>
          <div css={styles.header}>
            <Text fontWeight='bold'>
              “Por favor, responda as questões abaixo sobre o seu filho. Pense em como ele geralmente se comporta. Se
              você viu o seu filho apresentar o comportamento descrito poucas vezes, ou seja, se não for um
              comportamento frequente, então responda não.”
            </Text>
          </div>
          {Object.entries(MCHAT_PERGUNTAS).map(([perguntaEnum, perguntaText], index) => (
            <HFlow
              justifyContent='space-between'
              alignItems='center'
              style={styles.item}
              key={perguntaEnum}
              data-testid={`PerguntasMChatModal-${index + 1}`}
            >
              <Text component='p'>
                {index + 1}. {perguntaText}
              </Text>

              <SwitchButtonField<SimNaoEnum>
                name={meta[perguntaEnum as MChatPerguntaEnum]}
                options={[
                  { label: 'Sim', value: SimNaoEnum.SIM },
                  { label: 'Não', value: SimNaoEnum.NAO },
                ]}
              />
            </HFlow>
          ))}
        </VFlow>
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  headingWrapper: css`
    margin-top: -0.5rem;
  `,
  heading: css`
    margin-top: 1.5rem;
  `,
  listItemsBox: css`
    margin-left: 1.2rem;
  `,
  box: css`
    background: ${theme.pallete.surface.main};
    border-radius: 2px;
    border: 1px solid ${theme.pallete.divider};
    & > * {
      border-bottom: 1px solid ${theme.pallete.divider};

      &:last-child {
        border-bottom: 0;
      }
    }
  `,
  header: css`
    padding: 1rem;
    background: ${theme.pallete.gray.c90};
  `,
  item: css`
    padding: 1rem;
  `,
  accordionInformationPanel: css`
    padding-right: 1rem;
    padding-bottom: 1rem;
  `,
})
